import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { ShieldCheck, LogIn, EyeOff, Eye } from 'lucide-react';
import { BRAND_LOGO } from '@/lib/logos';

export default function LoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPwd, setShowPwd] = useState(false);
    const [loading, setLoading] = useState(false);
    const { loginUser } = useAuth();
    const navigate = useNavigate();

    const submit = async (e) => {
        e && e.preventDefault();
        const em = email.trim().toLowerCase();
        if (!em) { toast.error('Enter your email'); return; }
        if (!password) { toast.error('Enter your password'); return; }
        setLoading(true);
        try {
            const { data } = await api.post('/auth/login', { email: em, password });
            loginUser(data.token, data.user);
            toast.success('Welcome back!');
            navigate('/dashboard');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Login failed');
        } finally { setLoading(false); }
    };

    return (
        <PublicLayout>
            <div className="min-h-[70vh] flex items-center justify-center px-4 py-10">
                <Card className="max-w-md w-full p-6 sm:p-8 shadow-card" data-testid="login-card">
                    <div className="flex items-center gap-2 mb-1">
                        <img src={BRAND_LOGO} alt="Eagle Eyes" className="h-10 w-10 rounded-lg object-cover" />
                        <div>
                            <div className="font-display font-semibold text-lg">Sign in</div>
                            <div className="text-xs text-muted-foreground">Email &amp; password login</div>
                        </div>
                    </div>
                    <div className="mt-4 flex items-center gap-2 text-[12px] text-muted-foreground">
                        <ShieldCheck className="h-3.5 w-3.5" /> Your account is protected by email and WhatsApp OTP verification.
                    </div>
                    <form onSubmit={submit} className="mt-6 space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                data-testid="login-email-input"
                                type="email"
                                placeholder="you@example.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                autoComplete="email"
                                autoFocus
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="password">Password</Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    data-testid="login-password-input"
                                    type={showPwd ? 'text' : 'password'}
                                    placeholder="Enter password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    autoComplete="current-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPwd(s => !s)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                                    tabIndex={-1}
                                    aria-label={showPwd ? 'Hide password' : 'Show password'}
                                    data-testid="login-password-toggle"
                                >
                                    {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                        </div>
                        <Button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-[#0E2A47] hover:bg-[#0B223A]"
                            data-testid="login-submit-button"
                        >
                            <LogIn className="h-4 w-4 mr-2" />
                            {loading ? 'Signing in…' : 'Sign in'}
                        </Button>
                    </form>
                    <div className="mt-5 text-center text-sm text-muted-foreground">
                        New to Eagle Eyes?{' '}
                        <Link to="/register" className="text-[#1F9D8A] font-medium hover:underline" data-testid="login-register-link">
                            Create an account
                        </Link>
                    </div>
                    <div className="mt-2 text-center text-sm">
                        <Link to="/forgot-password?role=user" className="text-muted-foreground hover:text-foreground hover:underline" data-testid="login-forgot-password-link">Forgot password?</Link>
                    </div>
                    <div className="mt-6 pt-4 border-t border-border text-xs text-muted-foreground text-center">
                        Are you a reseller?{' '}
                        <Link to="/reseller/login" className="text-[#1F9D8A] font-medium" data-testid="login-reseller-link">Reseller login</Link>
                    </div>
                </Card>
            </div>
        </PublicLayout>
    );
}
